import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { isApiError } from '@missions/api-client';
import type { TenantPublicConfig } from '@missions/types';
import { BrandProvider, UIProvider } from '@missions/ui';
import '@missions/ui/styles.css';
import './styles/app.css';
import './styles/web.css';
import { App } from './App';
import { api } from './lib/client';
import { maybeStartMocks } from './lib/mocks';
import { registerPushWorker } from './lib/push';
import { applyTenantHead } from './lib/seo';
import { WorkspaceNotFound } from './pages/WorkspaceNotFound';
import { BootScreen, isWaking, withWakeRetry } from './lib/wake';

/** Product defaults: used on the claim/onboarding pages (no tenant yet) and when tenant config can't load. */
const PRODUCT: TenantPublicConfig = {
  tenantId: '00000000-0000-0000-0000-000000000000',
  slug: '',
  appTitle: 'Missions',
  tagline: 'Plan, assign & track work',
  brandColor: '#0457fd',
  fontKey: 'jakarta',
  logoUrl: null,
  logoDarkUrl: null,
  iconUrl: null,
  missionLanguage: true,
  showPoweredBy: true,
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      // cold starts (free tier) get more patient retries than ordinary failures
      retry: (n, e) => (isWaking(e) ? n < 5 : !(isApiError(e) && [401, 402, 403, 404, 422].includes(e.status)) && n < 2),
      retryDelay: (n) => Math.min(1500 * 1.6 ** n, 10_000),
    },
  },
});

const isPublicProductPage = () => /^\/(claim|offer|onboard)(\/|$)/.test(window.location.pathname);

async function boot(): Promise<void> {
  await maybeStartMocks();
  const root = createRoot(document.getElementById('root')!);
  root.render(<BootScreen />);
  let tenant: TenantPublicConfig = PRODUCT;
  if (!isPublicProductPage()) {
    try {
      // the free-tier API may be asleep: retry with backoff (~70s) behind a "Waking up…" screen
      tenant = await withWakeRetry(
        () => api.call('public.tenantConfig', { query: {} }),
        (ms) => {
          if (ms >= 1500) root.render(<BootScreen waking />);
        },
      );
    } catch (e) {
      if (isApiError(e) && e.status === 404) {
        root.render(<WorkspaceNotFound />);
        return;
      }
      if (isWaking(e)) {
        root.render(<BootScreen failed onRetry={() => window.location.reload()} />);
        return;
      }
    }
  }
  applyTenantHead(tenant);
  if (import.meta.env.VITE_USE_MOCKS !== 'true') void registerPushWorker();
  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrandProvider initial={{ color: tenant.brandColor, font: tenant.fontKey, missionLanguage: tenant.missionLanguage }}>
          <UIProvider build={__APP_BUILD__}>
            <App tenant={tenant} />
          </UIProvider>
        </BrandProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
}

void boot();
